import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  Form,
  FormField,
  Input,
  Textarea,
  Multiselect,
  Select,
  Toggle,
  Button,
  SpaceBetween,
  Container,
  Header,
  Box,
  Alert,
} from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import routes from '../../contstant/routePaths';
import { Category } from '../../types/helios/Category';
import { Tag } from '../../types/helios/Tags';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import useUserProfile from 'src/hooks/useUserProfile';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { CategoryFormValidator } from 'src/validations/CategoryFormValidator';
import { useCategories } from 'src/store/categories/useCategories';
import { useBusinessUnit } from 'src/store/businessUnit/useBusinessUnit';
import BusinessUnitListDropdown from '../businessUnit/BusinessUnitListDropdown';
import { useTags } from 'src/store/tags/useTags';
import permissionConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import { checkFeaturePermission } from 'src/utils/generalUtils';
import { useUsers } from 'src/store/users/useUsers';
import RequiredFieldLabel from '../RequiredFieldLabel';

interface QuestionCategoryDetailsFormProps {
  category?: Category | null;
  isEditing?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
}

interface CategoryParams {
  categoryName: string;
  description: string;
  businessUnitId: string;
  divisionName: string;
  requiredTags: string[];
  isActive: boolean;
}

const QuestionCategoryDetailsForm = memo(
  ({
    category,
    isEditing = false,
    onSave,
    onCancel,
    onEdit,
  }: QuestionCategoryDetailsFormProps) => {
    const navigate = useNavigate();
    const { closeView } = useSplitPanel();
    const { getUserId, getUserProgramId, getPersona, getUserBusinessUnitId } =
      useUserProfile();
    const {
      businessUnitList,
      businessUnitsWithDivisions,
      fetchBusinessUnitApiStatus,
      loadBusinessUnits,
      loadBusinessUnitsWithDivisions,
    } = useBusinessUnit();
    const { tagsList, fetchTagsApiStatus, loadTags } = useTags();

    const {
      createCategories,
      updateCategories,
      createCategoriesApiStatus,
      updateCategoriesApiStatus,
    } = useCategories();

    const defaultBusinessUnit =
      getPersona() === Persona.PLATFORM_ADMIN ? '' : getUserBusinessUnitId();
    // Initialize form values
    const initValues: CategoryParams = {
      categoryName: category?.categoryName || '',
      description: category?.description || '',
      businessUnitId: category?.businessUnit || defaultBusinessUnit,
      divisionName: category?.divisionName || '',
      requiredTags: category?.requiredTags?.map((tag) => tag.tagId) || [],
      isActive: category?.isActive ?? true,
    };

    const [categoryParams, setCategoryParams] = useState<CategoryParams>(initValues);

    // Form validation
    const { fieldValidationError, validate } = useFormValidation(
      categoryParams,
      CategoryFormValidator(category),
    );
    const { checkUserPermissions, userPermission } = useUsers();
    const isEditCategory = useCallback(() => {
      return checkFeaturePermission(
        getPersona(),
        permissionConfig.CATEGORY.EDIT_CATEGORY,
        userPermission?.permissions,
      );
    }, [userPermission?.permissions, getPersona]);

    const programId = getUserProgramId();

    useEffect(() => {
      setCategoryParams(initValues);
      setSaveTriggered(false); // Reset save trigger when form opens
    }, [category]);

    useEffect(() => {
      if (programId) {
        loadBusinessUnitsWithDivisions({
          programId: programId,
          isActive: true,
          isDeleted: false,
        });
      }
    }, [programId, loadBusinessUnitsWithDivisions]);

    // Set default business unit after business units are loaded
    useEffect(() => {
      if (
        !category &&
        businessUnitsWithDivisions.length > 0 &&
        !categoryParams.businessUnitId
      ) {
        const userBusinessUnitId = getUserBusinessUnitId();
        const userBusinessUnit = businessUnitsWithDivisions.find(
          (bu) => bu.businessUnitId === userBusinessUnitId,
        );

        if (userBusinessUnit) {
          setCategoryParams((prev) => ({
            ...prev,
            businessUnitId: userBusinessUnit.businessUnitId,
          }));
        }
      }
    }, [businessUnitsWithDivisions, category]);

    useEffect(() => {
      if (programId && categoryParams.businessUnitId) {
        loadTags({
          programId: programId,
          businessUnitId: categoryParams.businessUnitId,
          isActive: true,
        });
      }
    }, [categoryParams.businessUnitId, programId, loadTags]);

    // Handle successful API responses - only trigger after actual save operation
    const [saveTriggered, setSaveTriggered] = useState(false);

    useEffect(() => {
      if (
        createCategoriesApiStatus.isSuccess &&
        !createCategoriesApiStatus.isLoading &&
        onSave &&
        saveTriggered &&
        category === null
      ) {
        onSave();
        setSaveTriggered(false);
      }
    }, [createCategoriesApiStatus, onSave, saveTriggered, category]);

    useEffect(() => {
      if (
        updateCategoriesApiStatus.isSuccess &&
        !updateCategoriesApiStatus.isLoading &&
        onSave &&
        saveTriggered &&
        category !== null
      ) {
        onSave();
        setSaveTriggered(false);
      }
    }, [updateCategoriesApiStatus, onSave, saveTriggered, category]);

    const handleCancel = () => {
      if (onCancel) {
        onCancel();
      } else {
        closeView();
      }
    };

    const handleEdit = () => {
      if (onEdit) {
        onEdit();
      }
    };

    const handleSave = async () => {
      if (validate()) {
        setSaveTriggered(true); // Mark that save was triggered
        try {
          if (category) {
            // Update existing category
            const requiredTagObjects = categoryParams.requiredTags
              .map((tagId) => tagsList.find((tag) => tag.tagId === tagId))
              .filter((tag): tag is Tag => tag !== undefined)
              .filter((tag) => {
                const hasValidProgramId = !!(
                  tag.program ||
                  tag.programId ||
                  getUserProgramId()
                );
                if (!hasValidProgramId) {
                  console.warn('Filtering out tag with null programId:', tag.tagId);
                }
                return hasValidProgramId;
              })
              .map((tag) => ({
                tagId: tag.tagId.replace(/\s+/g, ''), // Remove all spaces
                tagGroupName: tag.tagGroupName,
                businessUnit: tag.businessUnit,
                program: tag.program || tag.programId || getUserProgramId(),
                description: tag.description,
                isActive: tag.isActive,
                values: tag.values,
                isMandatoryForQuestions: tag.isMandatoryForQuestions,
                isMandatoryForAuditTemplates: tag.isMandatoryForAuditTemplates,
                isApplicableForAuditTemplate: tag.isApplicableForAuditTemplate,
                maxTagsPerTemplate: tag.maxTagsPerTemplate,
                createdBy: tag.createdBy,
                lastUpdatedBy: tag.lastUpdatedBy || tag.createdBy || getUserId(),
              }));

            await updateCategories({
              categoryName: categoryParams.categoryName,
              description: categoryParams.description,
              businessUnitId: categoryParams.businessUnitId,
              divisionName: categoryParams.divisionName,
              requiredTags: requiredTagObjects as unknown as Tag[],
              isActive: categoryParams.isActive,
              updatedBy: getUserId(),
            });
          } else {
            // Create new category
            const requiredTagObjects = categoryParams.requiredTags
              .map((tagId) => tagsList.find((tag) => tag.tagId === tagId))
              .filter((tag): tag is Tag => tag !== undefined)
              .filter((tag) => {
                const hasValidProgramId = !!(
                  tag.program ||
                  tag.programId ||
                  getUserProgramId()
                );
                if (!hasValidProgramId) {
                  console.warn('Filtering out tag with null programId:', tag.tagId);
                }
                return hasValidProgramId;
              })
              .map((tag) => ({
                tagId: tag.tagId.replace(/\s+/g, ''), // Remove all spaces
                tagGroupName: tag.tagGroupName,
                businessUnit: tag.businessUnit,
                program: tag.program || tag.programId || getUserProgramId(),
                description: tag.description,
                isActive: tag.isActive,
                values: tag.values,
                isMandatoryForQuestions: tag.isMandatoryForQuestions,
                isMandatoryForAuditTemplates: tag.isMandatoryForAuditTemplates,
                isApplicableForAuditTemplate: tag.isApplicableForAuditTemplate,
                maxTagsPerTemplate: tag.maxTagsPerTemplate,
                createdBy: tag.createdBy,
                lastUpdatedBy: tag.lastUpdatedBy || tag.createdBy || getUserId(),
              }));

            const createData = {
              categoryName: categoryParams.categoryName,
              description: categoryParams.description,
              businessUnitId: categoryParams.businessUnitId,
              divisionName: categoryParams.divisionName,
              requiredTags: requiredTagObjects,
              isActive: categoryParams.isActive,
              createdBy: getUserId(),
            };
            createCategories({
              ...createData,
              requiredTags: requiredTagObjects as unknown as Tag[],
            });
          }
        } catch (error) {
          console.error('Error saving category:', error);
        }
      }
    };

    const renderActionButton = () => {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-testid="cancel-button"
            formAction="none"
            variant="link"
            onClick={handleCancel}
          >
            Cancel
          </Button>

          <Button
            data-testid="save-button"
            variant="primary"
            onClick={handleSave}
            loading={
              updateCategoriesApiStatus?.isLoading || createCategoriesApiStatus?.isLoading
            }
          >
            Save
          </Button>
        </SpaceBetween>
      );
    };

    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <Form actions={isEditCategory() && renderActionButton()}>
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label={<RequiredFieldLabel label="Category" />}
              errorText={fieldValidationError.categoryName}
              description={isEditing ? 'Enter the category name' : undefined}
            >
              <Input
                data-testid="category-field"
                value={categoryParams.categoryName || ''}
                onChange={({ detail }) =>
                  setCategoryParams((prev) => ({
                    ...prev,
                    categoryName: detail.value,
                  }))
                }
                readOnly={!!category} // Category name read-only for everyone when editing
              />
            </FormField>

            <FormField
              label="Description"
              errorText={fieldValidationError.description}
              description={
                isEditing || !category
                  ? 'Provide a description for this category (optional)'
                  : undefined
              }
            >
              <Textarea
                data-testid="description-field"
                value={categoryParams.description || ''}
                readOnly={!isEditCategory()}
                onChange={({ detail }) =>
                  setCategoryParams((prev) => ({
                    ...prev,
                    description: detail.value,
                  }))
                }
              />
            </FormField>

            <FormField label={<RequiredFieldLabel label="Business Unit" />}>
              <Input
                data-testid="businessUnit-input"
                value={
                  businessUnitsWithDivisions.find(
                    (bu) => bu.businessUnitId === categoryParams.businessUnitId,
                  )?.name || categoryParams.businessUnitId
                }
                readOnly
              />
            </FormField>

            <FormField
              label={<RequiredFieldLabel label="Division" />}
              errorText={fieldValidationError.divisionName}
              description={isEditing ? 'Select the division' : undefined}
            >
              {!!category ? (
                <Input
                  data-testid="division-field"
                  value={categoryParams.divisionName || ''}
                  readOnly
                />
              ) : (
                <Select
                  data-testid="division-field"
                  options={
                    businessUnitsWithDivisions
                      .find((bu) => bu.businessUnitId === categoryParams.businessUnitId)
                      ?.divisions?.map((division) => ({
                        label: division.divisionName,
                        value: division.divisionName,
                      })) || []
                  }
                  selectedOption={
                    categoryParams.divisionName
                      ? {
                          label: categoryParams.divisionName,
                          value: categoryParams.divisionName,
                        }
                      : null
                  }
                  onChange={({ detail }) => {
                    setCategoryParams((prev) => ({
                      ...prev,
                      divisionName: detail.selectedOption?.value || '',
                    }));
                  }}
                  placeholder="Choose division"
                />
              )}
            </FormField>

            <FormField
              label={<RequiredFieldLabel label="Required Tag Groups" />}
              errorText={fieldValidationError.requiredTags}
              description={
                isEditing || !category ? 'Select required tag groups' : undefined
              }
            >
              <Multiselect
                data-testid="required-tag-groups-field"
                options={tagsList.map((tag) => ({
                  label: tag.tagGroupName,
                  value: tag.tagId,
                }))}
                selectedOptions={
                  categoryParams.requiredTags?.map((tagId) => {
                    const tag = tagsList.find((t) => t.tagId === tagId);
                    return {
                      label: tag?.tagGroupName || 'Unknown Tag',
                      value: tagId,
                    };
                  }) || []
                }
                readOnly={!isEditCategory()}
                onChange={({ detail }) =>
                  setCategoryParams((prev) => ({
                    ...prev,
                    requiredTags: detail.selectedOptions
                      .map((option) => option.value)
                      .filter((value): value is string => value !== undefined),
                  }))
                }
                placeholder="Choose tag groups"
                expandToViewport
                filteringType="auto"
                loadingText="Loading tags..."
                statusType={fetchTagsApiStatus.isLoading ? 'loading' : 'finished'}
              />
            </FormField>

            <FormField
              label="Category Activation Status"
              constraintText="Enable or disable the category using this switch."
            >
              <Toggle
                data-testid="category-activation-status"
                checked={categoryParams.isActive ?? true}
                readOnly={!isEditCategory()}
                onChange={({ detail }) =>
                  setCategoryParams((prev) => ({ ...prev, isActive: detail.checked }))
                }
              >
                {categoryParams.isActive ? 'Active' : 'Inactive'}
              </Toggle>
            </FormField>

            {category && !isEditing && (
              <Button
                variant="primary"
                onClick={() =>
                  navigate(
                    `${routes.QUESTION.path}?category=${encodeURIComponent(category.categoryName)}`,
                  )
                }
              >
                View Questions
              </Button>
            )}
          </SpaceBetween>
        </Form>

        {(updateCategoriesApiStatus?.errorMessage ||
          createCategoriesApiStatus?.errorMessage) && (
          <Box margin={{ top: 'xl' }}>
            <Alert type="error">
              {updateCategoriesApiStatus?.errorMessage ||
                createCategoriesApiStatus?.errorMessage}
            </Alert>
          </Box>
        )}

        {(updateCategoriesApiStatus?.successMessage ||
          createCategoriesApiStatus?.successMessage) && (
          <Box margin={{ top: 'xl' }}>
            <Alert type="success">
              {updateCategoriesApiStatus?.successMessage ||
                createCategoriesApiStatus?.successMessage}
            </Alert>
          </Box>
        )}
      </form>
    );
  },
);

export default QuestionCategoryDetailsForm;
